import { Add, Delete } from '@mui/icons-material';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    IconButton,
    ListSubheader,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api';
import QuickFilamentDialog from './QuickFilamentDialog';
import { ColorSwatch, ColorSwatchLabel } from '../HtmlColorField';
import { filamentLabel, formatGrams } from '../../helpers/filaments';
import { emptyJobFilamentRow, sumFilamentRows } from '../../helpers/jobs';

const QUICK_CREATE_ID = '__quick_create__';
const ON_PRINTER_GROUP = 'Na impressora';
const IN_STOCK_GROUP = 'No estoque';
/** Cor única para destacar carretéis alocados na impressora selecionada. */
const ON_PRINTER_ACCENT = '#0f766e';
const ON_PRINTER_SOFT = 'rgba(15, 118, 110, 0.14)';
const ON_PRINTER_HEADER = 'rgba(15, 118, 110, 0.22)';

function optionLabel(option) {
    if (!option || typeof option === 'string') {
        return String(option || '');
    }
    if (option.uuid === QUICK_CREATE_ID) {
        return '';
    }
    if (option.archived) {
        const label = String(option.label || '').trim();
        return label ? `${label} (arquivado)` : 'Filamento arquivado';
    }
    return filamentLabel(option);
}

function filamentId(option) {
    return option?.id || option?.uuid || '';
}

/** Mantém a ordem de alocação na impressora; o restante segue depois. */
function prioritizePrinterFilaments(list, printerFilamentIds) {
    if (!printerFilamentIds?.length || !list?.length) {
        return list;
    }
    const rank = new Map(printerFilamentIds.map((id, index) => [id, index]));
    return [...list].sort((a, b) => {
        const ra = rank.has(filamentId(a)) ? rank.get(filamentId(a)) : Number.MAX_SAFE_INTEGER;
        const rb = rank.has(filamentId(b)) ? rank.get(filamentId(b)) : Number.MAX_SAFE_INTEGER;
        return ra - rb;
    });
}

/**
 * Seção de filamentos de uma impressão. Cada linha liga um carretel cadastrado à
 * quantidade que ele perdeu, e a soma precisa fechar com o total da impressão.
 */
export default function JobFilamentsFields({
    value,
    onChange,
    expectedGrams,
    error,
    usingActualConsumption,
    deductsStock = true,
    printerUuid,
}) {
    const rows = Array.isArray(value) && value.length ? value : [emptyJobFilamentRow()];
    const [filaments, setFilaments] = useState([]);
    const [printerFilamentIds, setPrinterFilamentIds] = useState([]);
    const [quickCreateIndex, setQuickCreateIndex] = useState(null);

    const loadFilaments = useCallback(() => {
        api.get('filaments', { params: { page: 1, limit: 200 } })
            .then((response) => {
                setFilaments(response.data?.materials || []);
            })
            .catch(() => {
                setFilaments([]);
            });
    }, []);

    useEffect(() => {
        loadFilaments();
    }, [loadFilaments]);

    useEffect(() => {
        if (!printerUuid) {
            setPrinterFilamentIds([]);
            return undefined;
        }
        let cancelled = false;
        api.get(`printers/${printerUuid}`)
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const assigned = response.data?.data?.filaments || [];
                const ordered = [...assigned]
                    .sort((a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0))
                    .map((item) => item.filament_uuid)
                    .filter(Boolean);
                setPrinterFilamentIds(ordered);
            })
            .catch(() => {
                if (!cancelled) {
                    setPrinterFilamentIds([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [printerUuid]);

    const byId = useMemo(() => {
        const map = new Map();
        filaments.forEach((filament) => {
            map.set(filament.id || filament.uuid, filament);
        });
        return map;
    }, [filaments]);

    const total = sumFilamentRows(rows);
    const expected = Number(expectedGrams) || 0;
    const remaining = expected - total;

    const patchRow = (index, patch) => {
        onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const addRow = () => onChange([...rows, emptyJobFilamentRow()]);

    const removeRow = (index) => {
        onChange(rows.length <= 1 ? [emptyJobFilamentRow()] : rows.filter((_, i) => i !== index));
    };

    const handleQuickCreated = (created) => {
        const normalized = {
            id: created.uuid,
            uuid: created.uuid,
            type: created.type,
            brand: created.brand,
            color: created.color,
            color_label: created.color_label,
            spool_weight: created.spool_weight,
            current_weight: created.current_weight,
        };
        setFilaments((prev) => [normalized, ...prev]);
        if (quickCreateIndex != null) {
            // A primeira linha já entra com o total da impressão; as seguintes
            // ficam zeradas para o usuário distribuir.
            patchRow(quickCreateIndex, {
                filament_uuid: created.uuid,
                label: filamentLabel(normalized),
                grams: rows[quickCreateIndex]?.grams || (quickCreateIndex === 0 ? String(expected) : ''),
            });
        }
        setQuickCreateIndex(null);
    };

    // Opções já usadas em outra linha saem da lista: um rolo por linha.
    const usedIds = new Set(rows.map((row) => row.filament_uuid).filter(Boolean));

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderColor: error ? 'error.main' : undefined,
                borderWidth: error ? 2 : 1,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1">Filamentos utilizados</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        size="small"
                        label={`${formatGrams(total)} de ${formatGrams(expected)}`}
                        color={total === expected ? 'success' : 'default'}
                        variant={total === expected ? 'filled' : 'outlined'}
                    />
                    {remaining !== 0 && (
                        <Typography variant="caption" color="text.secondary">
                            {remaining > 0
                                ? `faltam ${formatGrams(remaining)}`
                                : `${formatGrams(-remaining)} a mais`}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A soma das quantidades precisa fechar com {usingActualConsumption ? 'o consumo real' : 'o filamento estimado'}
                {' '}da impressão. O peso informado aqui é debitado do saldo de cada rolo.
            </Typography>

            {!deductsStock && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Enquanto a impressão estiver em andamento nada é debitado do estoque, e o vínculo é
                    opcional. Ao mudar o status para um desfecho, o filamento passa a ser exigido e o
                    saldo dos carretéis é ajustado.
                </Alert>
            )}

            {rows.map((row, index) => {
                // Um carretel arquivado não vem na lista de ativos, mas a impressão
                // que o consumiu continua apontando para ele. A linha reconstrói a
                // opção a partir do que já foi gravado para não perder o vínculo.
                const archivedSelection = row.filament_uuid && !byId.has(row.filament_uuid)
                    ? { id: row.filament_uuid, uuid: row.filament_uuid, archived: true, label: row.label }
                    : null;
                const selected = byId.get(row.filament_uuid) || archivedSelection;
                const options = prioritizePrinterFilaments(
                    filaments.filter((filament) => {
                        const id = filament.id || filament.uuid;
                        return id === row.filament_uuid || !usedIds.has(id);
                    }),
                    printerFilamentIds,
                );
                if (archivedSelection) {
                    options.unshift(archivedSelection);
                }

                return (
                    <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'flex-start' }}>
                        <Autocomplete
                            fullWidth
                            options={[...options, { uuid: QUICK_CREATE_ID, id: QUICK_CREATE_ID }]}
                            value={selected}
                            getOptionLabel={optionLabel}
                            isOptionEqualToValue={(option, val) => (option?.id || option?.uuid) === (val?.id || val?.uuid)}
                            groupBy={(option) => {
                                if (option.uuid === QUICK_CREATE_ID || !printerFilamentIds.length) {
                                    return '';
                                }
                                if (printerFilamentIds.includes(filamentId(option))) {
                                    return ON_PRINTER_GROUP;
                                }
                                return IN_STOCK_GROUP;
                            }}
                            renderGroup={(params) => {
                                const onPrinter = params.group === ON_PRINTER_GROUP;
                                return (
                                    <li key={params.key}>
                                        {params.group ? (
                                            <ListSubheader
                                                component="div"
                                                sx={{
                                                    position: 'sticky',
                                                    top: -8,
                                                    zIndex: 1,
                                                    lineHeight: 2.4,
                                                    px: 1.5,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 800,
                                                    letterSpacing: 0.6,
                                                    textTransform: 'uppercase',
                                                    color: onPrinter ? ON_PRINTER_ACCENT : 'text.secondary',
                                                    bgcolor: onPrinter ? ON_PRINTER_HEADER : 'action.hover',
                                                    borderBottom: onPrinter
                                                        ? `2px solid ${ON_PRINTER_ACCENT}`
                                                        : '1px solid',
                                                    borderColor: onPrinter ? ON_PRINTER_ACCENT : 'divider',
                                                }}
                                            >
                                                {params.group}
                                            </ListSubheader>
                                        ) : null}
                                        <ul
                                            style={{
                                                padding: 0,
                                                margin: 0,
                                                ...(onPrinter
                                                    ? {
                                                        backgroundColor: ON_PRINTER_SOFT,
                                                        borderLeft: `4px solid ${ON_PRINTER_ACCENT}`,
                                                    }
                                                    : {}),
                                            }}
                                        >
                                            {params.children}
                                        </ul>
                                    </li>
                                );
                            }}
                            filterOptions={(opts, state) => {
                                const query = String(state.inputValue || '').trim().toLowerCase();
                                const quick = opts.filter((o) => o.uuid === QUICK_CREATE_ID);
                                const real = prioritizePrinterFilaments(
                                    opts.filter((o) => o.uuid !== QUICK_CREATE_ID),
                                    printerFilamentIds,
                                );
                                if (!query) {
                                    return [...real, ...quick];
                                }
                                return [
                                    ...real.filter((item) => filamentLabel(item).toLowerCase().includes(query)),
                                    ...quick,
                                ];
                            }}
                            renderOption={(props, option) => {
                                const { key, ...rest } = props;
                                if (option.uuid === QUICK_CREATE_ID) {
                                    return (
                                        <li key={QUICK_CREATE_ID} {...rest}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                                                <Add fontSize="small" />
                                                Cadastrar novo filamento
                                            </Box>
                                        </li>
                                    );
                                }
                                if (option.archived) {
                                    return (
                                        <li key={option.id || option.uuid} {...rest}>
                                            <Typography variant="body2" color="text.secondary">
                                                {optionLabel(option)}
                                            </Typography>
                                        </li>
                                    );
                                }
                                const onPrinter = printerFilamentIds.includes(filamentId(option));
                                return (
                                    <li key={option.id || option.uuid} {...rest}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                            <ColorSwatchLabel
                                                color={option.color}
                                                label={filamentLabel(option)}
                                            />
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: (Number(option.current_weight) || 0) <= 0
                                                        ? 'error.main'
                                                        : onPrinter
                                                            ? ON_PRINTER_ACCENT
                                                            : 'text.secondary',
                                                    fontWeight: onPrinter ? 600 : 400,
                                                }}
                                            >
                                                Saldo: {formatGrams(option.current_weight)}
                                                {onPrinter ? ' · na impressora' : ''}
                                            </Typography>
                                        </Box>
                                    </li>
                                );
                            }}
                            onChange={(_, newValue) => {
                                if (newValue?.uuid === QUICK_CREATE_ID) {
                                    setQuickCreateIndex(index);
                                    return;
                                }
                                if (!newValue) {
                                    patchRow(index, { filament_uuid: '', label: '' });
                                    return;
                                }
                                patchRow(index, {
                                    filament_uuid: newValue.id || newValue.uuid,
                                    label: filamentLabel(newValue),
                                    // A primeira linha assume o total da impressão;
                                    // as seguintes entram zeradas.
                                    grams: row.grams || (index === 0 ? String(expected) : ''),
                                });
                            }}
                            noOptionsText="Nenhum filamento cadastrado"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={`Filamento ${index + 1}`}
                                    error={Boolean(error) && !row.filament_uuid}
                                    InputProps={{
                                        ...params.InputProps,
                                        // A cor do carretel escolhido continua visível depois
                                        // que a lista fecha, que é quando o usuário confere
                                        // se pegou o rolo certo.
                                        startAdornment: (
                                            <>
                                                {selected && !selected.archived && (
                                                    <ColorSwatch color={selected.color} sx={{ ml: 0.5 }} />
                                                )}
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                    }}
                                    helperText={selected?.archived
                                        ? 'Carretel arquivado, mantido aqui para preservar o registro desta impressão.'
                                        : selected
                                            ? `Saldo atual: ${formatGrams(selected.current_weight)}`
                                            : 'Busque um filamento cadastrado ou cadastre um novo.'}
                                />
                            )}
                        />
                        <Box sx={{ width: { xs: 110, sm: 150 }, flexShrink: 0 }}>
                            <TextField
                                label="Utilizado (g)"
                                fullWidth
                                type="number"
                                inputProps={{ min: 0, inputMode: 'numeric' }}
                                value={row.grams}
                                onChange={(e) => patchRow(index, { grams: e.target.value })}
                                error={Boolean(error)}
                            />
                        </Box>
                        <IconButton
                            color="error"
                            onClick={() => removeRow(index)}
                            aria-label="remover filamento"
                            sx={{ mt: 1 }}
                        >
                            <Delete />
                        </IconButton>
                    </Box>
                );
            })}

            <Button startIcon={<Add />} onClick={addRow} type="button">
                Adicionar filamento
            </Button>

            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            <QuickFilamentDialog
                open={quickCreateIndex != null}
                onClose={() => setQuickCreateIndex(null)}
                onCreated={handleQuickCreated}
            />
        </Paper>
    );
}
